import { useUserSettings } from "@/contexts"
import type { Meter } from "@/models/meter"
import type { Organization } from "@/models/organization"
import type { ResourceName } from "@/models/resource"
import { useQueries } from "@tanstack/react-query"

import {
  type IntervalResourceProbe,
  type IntervalResourceScope,
  getResourceCandidates,
  toIntervalResourceScope,
} from "./interval-resource-scope"
import {
  INTERVAL_RESOURCE_DISCOVERY_LIMIT,
  INTERVAL_USAGE_LIST_FILTERS,
  toMeterIdsQueryParam,
} from "./interval-utils"
import { buildIntervalsQuery } from "./intervals-query"

interface UseIntervalResourceScopeOptions {
  meters: Meter[] | undefined
  metersNotReady: boolean
  organizationId: Organization["id"]
  selectedResource: ResourceName | null
  siteIdNumber: number | undefined
}

export const useIntervalResourceScope = ({
  meters,
  metersNotReady,
  organizationId,
  selectedResource,
  siteIdNumber,
}: UseIntervalResourceScopeOptions): IntervalResourceScope => {
  const { currency } = useUserSettings()
  const readyMeters = metersNotReady ? [] : meters
  const candidates = getResourceCandidates(readyMeters)

  const queries = useQueries({
    queries: candidates.map((candidate) => ({
      ...buildIntervalsQuery({
        apiQueryOptions: {
          ...INTERVAL_USAGE_LIST_FILTERS,
          limit: INTERVAL_RESOURCE_DISCOVERY_LIMIT,
          meter_ids: toMeterIdsQueryParam(candidate.meters),
          site_id: siteIdNumber,
          sort: "start_datetime:desc",
        },
        currency,
        enabled: !metersNotReady && candidate.meters.length > 0,
        organizationId,
        pageThrough: false,
      }),
      retry: false,
      throwOnError: false,
    })),
  })

  const probes: IntervalResourceProbe[] = candidates.map((candidate, index) => {
    const query = queries[index]

    return {
      isError: query?.isError ?? false,
      isLoading: query?.isLoading ?? false,
      items: query?.data?.items ?? [],
      resource: candidate.resource,
    }
  })

  return toIntervalResourceScope({
    meters: readyMeters,
    metersNotReady,
    probes,
    selectedResource,
  })
}
